import {action, computed, observable, runInAction} from 'mobx';
import each from 'lodash/each';
import forEach from 'lodash/forEach';
import last from 'lodash/last';
import find from 'lodash/find';
import get from 'lodash/get';
import filter from 'lodash/filter';
import some from 'lodash/some';
import assign from 'lodash/assign';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import {RECORDING_MODES} from '@techsee/techsee-common/lib/constants/room.constants';
import {RECORDING_MEDIA_TYPE} from '@techsee/techsee-common/lib/constants/recording.constants';
import {PlatformType} from '@techsee/techsee-common/lib/constants/utils.constant';
import tsDate from '@techsee/techsee-date';
import patterns from '@techsee/techsee-common/lib/constants/utils.patterns';
import {TechseeBaseController} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-controller';
import {HistoryState, IHistoryRecord, IFilteredData, ALL_RECORDING_MEDIA_TYPES} from './_contracts/HistoryState';
import {ChangeSettingsModalController} from '../../modals/change-settings-modal/ChangeSettingsModal.controller';
import {ChangeAccountModalController} from '../../modals/change-account-modal/ChangeAccountModal.controller';
import {RouteName} from '../../router/route-names';
import {ChangeSettingsModalState} from '../../modals/change-settings-modal/ChangeSettingsModal.state';
import {ChangeAccountModalState} from '../../modals/change-account-modal/ChangeAccountModal.state';
import {ITsAppUserService} from '../../services/TsAppUserService';
import {IAuthService} from '../../services/auth/auth.service';
import {AppState} from '../../app.state';

import {IHistoryService, IVideoRecordingsService} from './_contracts/HistoryServiceContracts';
import {IRedirectionService} from '../../services/RedirectionService';

export interface IHistoryControllerDependencies {
    historyService: IHistoryService;
    videoRecordingsService: IVideoRecordingsService;
    accountData: any;
    tsStateHelper: any;
    tsUrlUtils: any;
    tsSessionData: any;
    browserUtilsService: any;
    redirectionService: IRedirectionService;
    tsAppUserService: ITsAppUserService;
    authService: IAuthService;
    appState: AppState;
    displayTabletAsDesktop: boolean;
    currentUser: any;
}

export class HistoryController extends TechseeBaseController {
    readonly state: HistoryState;

    private readonly historyService: IHistoryService;

    private readonly videoRecordingsService: IVideoRecordingsService;

    readonly accountData: any;

    private readonly stateHelper: any;

    private readonly urlUtils: any;

    private readonly tsSessionData: any;

    private readonly browserUtilsService: any;

    private readonly redirectionService: IRedirectionService;

    private readonly tsAppUserService: ITsAppUserService;

    private readonly authService: IAuthService;

    private readonly appState: AppState;

    private readonly displayTabletAsDesktop: boolean;

    readonly currentUser: any;

    private readonly timezone: string;

    public readonly searchTags: boolean;

    public readonly allLanguagesNoHtml: RegExp;

    @observable
    changeSettingsModalController?: ChangeSettingsModalController;

    @observable
    changeAccountModalController?: ChangeAccountModalController;

    constructor(state: HistoryState, dependencies: IHistoryControllerDependencies) {
        super();
        this.state = state;
        this.historyService = dependencies.historyService;
        this.videoRecordingsService = dependencies.videoRecordingsService;
        this.accountData = dependencies.accountData;
        this.stateHelper = dependencies.tsStateHelper;
        this.urlUtils = dependencies.tsUrlUtils;
        this.tsSessionData = dependencies.tsSessionData;
        this.browserUtilsService = dependencies.browserUtilsService;
        this.redirectionService = dependencies.redirectionService;
        this.tsAppUserService = dependencies.tsAppUserService;
        this.authService = dependencies.authService;
        this.appState = dependencies.appState;
        this.displayTabletAsDesktop = dependencies.displayTabletAsDesktop;
        this.currentUser = dependencies.currentUser;

        const customerId = this.urlUtils.getParamValue('customerId') || this.urlUtils.getParamValue('r');
        let customerNumber = this.urlUtils.getParamValue('customerNumber') || this.urlUtils.getParamValue('p');
        const countryCode = this.urlUtils.getParamValue('c');
        const finalCustomerNumber = countryCode ? countryCode + customerNumber : customerNumber;

        runInAction(() => {
            this.state.customerId = customerId || undefined;
            this.state.customerNumber = finalCustomerNumber || undefined;
            this.state.searchByTags = !!this.urlUtils.getParamValue('isSearchByTags');
        });

        this.timezone = this.accountData?.settings?.timezone || 'UTC';
        this.searchTags = this.accountData?.protectedSettings
            ? this.accountData.protectedSettings.imageTagging && this.accountData.protectedSettings.searchTags
            : false;
        this.allLanguagesNoHtml = patterns.allLanguagesNoHtml;

        // Read filter from URL and apply initial filter
        const filterParam = this.urlUtils.getParamValue('filter') || '';
        const filter = filterParam.split(',').reduce((acc: any, val: string) => {
            const [key, value] = val.split(':');
            if (key && value) acc[key] = value;
            return acc;
        }, {});
        this._applyInitialFilterFromUrl(filter);

        if (!this.state.searchByTags) {
            this._getHistoryData();
        }
    }

    onBackToInvite = (): void => {
        if (this.accountData?.protectedSettings?.v3?.enabled) {
            this.redirectionService.goToPortal();
        } else {
            this.redirectionService.goToInvitePage({});
        }
    };

    getSearchByTags(): boolean {
        return this.state.searchByTags;
    }

    private _getCustomerId(): string | undefined {
        return this.state.customerId;
    }

    private _getCustomerNumber(): string | undefined {
        return this.state.customerNumber;
    }

    private _getSearchByTags(): boolean {
        return this.state.searchByTags;
    }

    @action
    private _applyInitialFilterFromUrl(filter: Record<string, string>): void {
        if (filter.start) {
            this.state.startDate = tsDate(filter.start, 'x').toDate();
        }

        if (filter.end) {
            this.state.endDate = tsDate(filter.end, 'x').toDate();
        }

        if (filter.tag) {
            this.state.searchTagForm.searchTerm = filter.tag;
            this.state.searchTagForm.submitted = true;

            return this.searchForTags(this.state.searchTagForm);
        }
    }

    @action
    setCurrentVideoToPlay = (videoLink: string): void => {
        this.state.videoLink = videoLink;
    };

    @action
    closeVideo = (): void => {
        this.state.videoLink = null;
    };

    getFormattedDate(dateString: string): Date {
        const dateMomentObject = tsDate(dateString, 'DD/MM/YYYY');
        const dateObject = dateMomentObject.toDate();

        return dateObject;
    }

    executeSearchByTags = async (tags: string, fromDate: string, toDate: string, page: number): Promise<void> => {
        const tagsArr = tags?.split(',');
        const nonEmptyTagsArray = tagsArr.filter((tag) => tag.trim());
        const encodedTags = nonEmptyTagsArray.map((tag) => encodeURIComponent(tag));

        const results = await this.historyService.searchByTags!({
            tags: JSON.stringify(encodedTags),
            accountId: this.accountData?._id,
            fromDate: this.getFormattedDate(fromDate),
            toDate: this.getFormattedDate(toDate),
            page: page,
            perPage: 20
        });

        this._updateFilteredDataFromSearch(results, tags);
    };

    @action
    private _updateFilteredDataFromSearch(results: any[], tags: string): void {
        const tagsArray = tags.split(',').map((tag) => tag.trim());
        const filteredRecords: IHistoryRecord[] = [];
        let totalImages = 0;

        each(results, (record) => {
            const filteredImages = record.images.filter((image: any) =>
                image.tags.some((tag: any) => tagsArray.includes(tag.text))
            );

            if (filteredImages.length > 0) {
                const updatedRecord = assign({}, record, {images: filteredImages});
                filteredRecords.push(updatedRecord);
                totalImages += filteredImages.length;
            }
        });

        this.state.filteredData.records = filteredRecords;
        this.state.filteredData.images = totalImages;
    }

    getSearchByTagsCount = async (tags: string, fromDate: string, toDate: string): Promise<any> => {
        const tagsArr = tags?.split(',');
        const nonEmptyTagsArray = tagsArr.filter((tag) => tag.trim());
        const encodedTags = nonEmptyTagsArray.map((tag) => encodeURIComponent(tag));

        const results = await this.historyService.searchByTagsCount!({
            tags: JSON.stringify(encodedTags),
            accountId: this.accountData?._id,
            fromDate: this.getFormattedDate(fromDate),
            toDate: this.getFormattedDate(toDate)
        });

        runInAction(() => {
            this.state.searchByTagsCount = results.matches;
        });

        return results;
    };

    searchByTagsParamsAreValid(tags: string): boolean {
        return (
            Boolean(tags && tags.length > 0) &&
            this.dateIsValid(this.state.startDate) &&
            this.dateIsValid(this.state.endDate)
        );
    }

    dateIsValid(date: Date | null): boolean {
        if (!date) {
            return false;
        }

        const dateRegex =
            /^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/;

        return dateRegex.test(tsDate(date).format('DD/MM/YYYY'));
    }

    sessionEndOrRecFallback(recording: {sessionStartTime: string; sessionEndTime: string; recEndTime: string}): string {
        const start = tsDate(recording.sessionStartTime);
        const end = tsDate(recording.sessionEndTime);

        return end.isValid() && end.isAfter(start) ? recording.sessionEndTime : recording.recEndTime;
    }

    calculateAndFormatDuration(startDate: string, endDate: string): string {
        const start = tsDate(startDate);
        const end = tsDate(endDate);

        if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
            return '0:00:00';
        }

        const duration = tsDate.duration(end.diff(start));
        const formattedMinutesSeconds = tsDate.utc(duration.asMilliseconds()).format('mm:ss');
        let hours = duration.hours();
        const days = duration.days();

        if (days > 0) {
            hours += 24 * days;
        }

        if (hours < 10) {
            hours = Number(`0${hours}`);
        }

        return `${hours}:${formattedMinutesSeconds}`;
    }

    @action
    _createThumbnailsForVideos(historyData: any, videoRecordingData: any[]): void {
        this.state.historyData = historyData || {records: []};

        if (videoRecordingData && videoRecordingData.length) {
            this._mergeVideosToHistory(videoRecordingData);
        }

        this._applyInitialFilter();
    }

    displayRecordingHistoryDates(date: string): string {
        return tsDate.utc(date).tz(this.timezone).format('DD.MM.YYYY | h:mm A');
    }

    displayDates(date: string): string {
        return tsDate.utc(date).tz(this.timezone).format('MMMM D, YYYY h:mm A');
    }

    displayEditDates(date: string): string {
        return tsDate.utc(date).tz(this.timezone).format('DD/MM/YY h:mm A');
    }

    @action
    _addNewHistoryRecord(recording: any): void {
        const recordToAdd: IHistoryRecord = {
            accountId: recording.accountId,
            customerId: recording.customerId,
            customerNumber: recording.customerNumber,
            files: [],
            images: [],
            messages: [],
            roomClosed: true,
            roomId: {_id: recording.sessionId},
            timestamp: recording.sessionStartTime,
            videos: []
        };

        this._addRecording(recording, recordToAdd);

        this.state.historyData.records.push(recordToAdd);
    }

    @action
    _addRecording(recording: any, historyRecord: IHistoryRecord): void {
        if (recording.type === RECORDING_MODES.AUTOMATIC) {
            if (!historyRecord.recordings) {
                historyRecord.recordings = [];
            }

            historyRecord.recordings.push(recording);
        } else {
            if (!this.state.historyData.hasVideoRecordings) {
                this.state.historyData.hasVideoRecordings = true;
            }

            recording.url = recording.link;
            recording.isRecordedVideo = recording.link;
            historyRecord.videos.push(recording);
        }
    }

    @action
    _mergeVideosToHistory(videos: any[]): void {
        if (!this.state.historyData.records) {
            this.state.historyData.records = [];
        }

        if (videos && videos.length) {
            each(videos, (recording) => {
                const historyRecord = find(
                    this.state.historyData.records,
                    (record) => get(record, 'roomId._id') === recording.sessionId
                );

                if (isEmpty(historyRecord)) {
                    this._addNewHistoryRecord(recording);
                } else {
                    this._addRecording(recording, historyRecord);
                }
            });
        }
    }

    async _getHistoryData(): Promise<void> {
        const promises: Promise<any>[] = [];

        const cachedSessionHistory = this.browserUtilsService.getFromSessionStorage('sessionHistory');
        const cachedVideoRecordingHistory = this.browserUtilsService.getFromSessionStorage('videoRecordingsHistory');

        if (cachedSessionHistory && cachedVideoRecordingHistory) {
            this.browserUtilsService.removeFromSessionStorage('sessionHistory');
            this.browserUtilsService.removeFromSessionStorage('videoRecordingsHistory');

            const cachedHistoryData = cachedSessionHistory?.data ?? cachedSessionHistory ?? {records: []};

            return this._createThumbnailsForVideos(cachedHistoryData, cachedVideoRecordingHistory);
        } else {
            const customerId = this._getCustomerId();
            const customerNumber = this._getCustomerNumber();

            if (customerId) {
                promises.push(
                    this.historyService.byCustomerId({
                        params: {
                            customerId,
                            accountId: this.accountData?._id
                        }
                    }),
                    this.videoRecordingsService.byCustomerId({
                        params: {
                            customerId
                        }
                    })
                );
            } else if (customerNumber) {
                promises.push(
                    this.historyService.byCustomerNumber({
                        params: {
                            customerNumber,
                            accountId: this.accountData?._id
                        }
                    }),
                    this.videoRecordingsService.byCustomerNumber({
                        params: {
                            customerNumber
                        }
                    })
                );
            }
        }

        if (promises.length === 0) {
            return;
        }

        const [recordHistory, videoRecordingHistory] = await Promise.all(promises);

        const videoRecordingsData = videoRecordingHistory?.data || videoRecordingHistory || [];

        runInAction(() => {
            this.state.videoRecordings = videoRecordingsData;
        });

        const historyDataToPass = recordHistory?.data ?? recordHistory ?? {records: []};

        return this._createThumbnailsForVideos(historyDataToPass, videoRecordingsData);
    }

    @action
    _applyInitialFilter(): void {
        const filterParam = this.urlUtils.getParamValue('filter') || '';
        const filter = filterParam.split(',').reduce((acc: any, val: string) => {
            const [key, value] = val.split(':');
            if (key && value) acc[key] = value;
            return acc;
        }, {});

        if (filter.start) {
            this.state.startDate = tsDate(filter.start, 'x').toDate();
        }

        if (filter.end) {
            this.state.endDate = tsDate(filter.end, 'x').toDate();
        }

        if (filter.tag) {
            this.state.searchTagForm.searchTerm = filter.tag;
            this.state.searchTagForm.submitted = true;

            return this.searchForTags(this.state.searchTagForm);
        }

        this.applyRange();
    }

    @action
    applyRange(): void {
        const filteredData: IFilteredData = {
            records: [],
            images: 0,
            videos: 0,
            messages: 0,
            files: 0,
            recordings: 0
        };

        const startTimestamp = this.state.startDate ? tsDate(this.state.startDate).startOf('day').unix() : 0;
        const endTimestamp = this.state.endDate ? tsDate(this.state.endDate).endOf('day').unix() : Infinity;

        each(this.state.historyData.records, (record) => {
            const recordTimestamp = tsDate(record.timestamp).utc().unix();

            if (startTimestamp <= recordTimestamp && endTimestamp >= recordTimestamp) {
                filteredData.images += record.images.length;
                filteredData.videos += record.videos.length;
                filteredData.messages += record.messages.length;
                filteredData.files += record.files.length;

                if (record.recordings && record.recordings.length) {
                    filteredData.recordings += record.recordings.length;
                }

                filteredData.records.push(record);
            }
        });

        this.state.filteredData = filteredData;
        this.state.recordings = this._getAllRecordings(this.state.filteredData.records);
    }

    _getAllRecordings(records: IHistoryRecord[]): any[] {
        const recordings: any[] = [];
        const selected = this.state.selectedMediaTypes;

        each(records, (record) => {
            if (record.recordings && record.recordings.length) {
                each(record.recordings, (recording) => {
                    const mediaType = recording.mediaType || RECORDING_MEDIA_TYPE.VIDEO_AND_AUDIO;
                    if (selected.indexOf(mediaType) !== -1) {
                        recordings.push(recording);
                    }
                });
            }
        });

        return this._sortByVideoDate(recordings);
    }

    getRecordingTypeLabelKey(mediaType?: string): string {
        switch (mediaType) {
            case RECORDING_MEDIA_TYPE.VIDEO:
                return 'HISTORY.VIEW.RECORDING_TYPE.VIDEO';
            case RECORDING_MEDIA_TYPE.AUDIO:
                return 'HISTORY.VIEW.RECORDING_TYPE.AUDIO';
            case RECORDING_MEDIA_TYPE.VIDEO_AND_AUDIO:
            default:
                return 'HISTORY.VIEW.RECORDING_TYPE.VIDEO_AND_AUDIO';
        }
    }

    getRecordingTypeFilterOptions(): {value: string; labelKey: string}[] {
        return ALL_RECORDING_MEDIA_TYPES.map((value) => ({
            value,
            labelKey: this.getRecordingTypeLabelKey(value)
        }));
    }

    isMediaTypeSelected(mediaType: string): boolean {
        return this.state.selectedMediaTypes.indexOf(mediaType) !== -1;
    }

    @action
    toggleMediaTypeFilter = (mediaType: string): void => {
        const idx = this.state.selectedMediaTypes.indexOf(mediaType);
        if (idx === -1) {
            this.state.selectedMediaTypes.push(mediaType);
        } else {
            this.state.selectedMediaTypes.splice(idx, 1);
        }
        this.state.recordings = this._getAllRecordings(this.state.filteredData.records);
    };

    _sortByVideoDate(videos: any[]): any[] {
        return videos.sort((a, b) => new Date(b.sessionStartTime).valueOf() - new Date(a.sessionStartTime).valueOf());
    }

    @action
    searchForTags = (form: {searchTerm: string; submitted: boolean}): void => {
        if (!form.searchTerm) {
            this.state.emptySearchTermSubmitted = true;

            return;
        }

        this.state.emptySearchTermSubmitted = false;
        this.applyRange();

        const filteredData: IFilteredData = {
            records: [],
            images: 0,
            videos: 0,
            messages: 0,
            files: 0,
            recordings: 0
        };

        const taggedRecords: IHistoryRecord[] = [];

        forEach(this.state.filteredData.records, (record) => {
            const filteredRecord = this._findTaggedResources(record, form.searchTerm);

            if (filteredRecord && (filteredRecord.images.length > 0 || filteredRecord.videos.length > 0)) {
                filteredData.images += filteredRecord.images.length;
                filteredData.videos += filteredRecord.videos.length;
                filteredData.messages += filteredRecord.messages.length;
                filteredData.files += filteredRecord.files.length;

                taggedRecords.push(filteredRecord);
            }
        });

        filteredData.records = taggedRecords;
        this.state.filteredData = filteredData;
        this.state.recordings = this._getAllRecordings(this.state.filteredData.records);
    };

    _findTaggedResources(record: IHistoryRecord, searchTerm: string): IHistoryRecord | null {
        const search = new RegExp(searchTerm.replace(/[-[\]{}()*+?.,\\/^$|#\s]/g, '\\$&'), 'i');

        const images = filter(record.images, (resource) => some(resource.tags, (tag) => search.test(tag.text)));

        const videos = filter(record.videos, (resource) => some(resource.tags, (tag) => search.test(tag.text)));

        if (images.length === 0 && videos.length === 0) {
            return null;
        }

        return assign({}, record, {images, videos});
    }

    @action
    clearSearchForm = (): void => {
        this.state.emptySearchTermSubmitted = false;

        if (this.state.searchTagForm) {
            this.state.searchTagForm.searchTerm = '';
            this.state.searchTagForm.submitted = false;
        }

        this.applyRange();
    };

    loadDashboard = (imageId: string | null, videoId: string | null, roomRecord: any): void => {
        const customerId = this._getCustomerId();
        const customerNumber = this._getCustomerNumber();
        const idQuery = customerId && `customerId:${encodeURIComponent(customerId)}`;
        const numberQuery = customerNumber && `customerNumber:${customerNumber}`;
        let returnQuery = idQuery || numberQuery;

        // Tell the dashboard we are coming from history page
        this.urlUtils.setParamValue('history', true);

        this.tsSessionData.setValue('roomId', roomRecord._id).then(async () => {
            if (this._getSearchByTags()) {
                returnQuery = 'searchByTags';
            } else {
                if (
                    this.state.searchTagForm &&
                    this.state.searchTagForm.searchTerm &&
                    this.state.searchTagForm.submitted
                ) {
                    returnQuery += `,tag:${this.state.searchTagForm.searchTerm}`;
                }

                if (this.state.startDate) {
                    returnQuery += `,start:${this.state.startDate.valueOf()}`;
                }

                if (this.state.endDate) {
                    returnQuery += `,end:${this.state.endDate.valueOf()}`;
                }

                if (imageId) {
                    this.urlUtils.setParamValue('loadImage', imageId);
                }

                if (videoId) {
                    this.urlUtils.setParamValue('loadVideo', videoId);
                }
            }

            if (returnQuery) {
                this.urlUtils.setParamValue('returnQuery', returnQuery);
            }

            const showRoomId = this.accountData?.protectedSettings?.showRoomIdInUrl;
            const roomId = showRoomId ? roomRecord._id : null;

            if (this.roomIsActiveOffline(roomRecord)) {
                return await this.stateHelper.navigateTo(RouteName.DASHBOARD_ENTRY, {
                    history: true,
                    room: roomId,
                    loadImage: imageId,
                    loadVideo: videoId,
                    returnQuery: returnQuery
                });
            }

            this.urlUtils.setParamValue('offline', 'true');

            await this.stateHelper.navigateTo(RouteName.DASHBOARD_MAIN, {
                offline: true,
                history: true,
                room: roomId,
                loadImage: imageId,
                loadVideo: videoId,
                returnQuery: returnQuery
            });
        });
    };

    roomIsActiveOffline(room: any): boolean {
        return room && room.offline && !tsDate(room.endTimestamp).unix();
    }

    loadRoom = (roomRecord: any, images: any[], videos: any[]): void => {
        const image = last(images) || {};
        const video = last(videos) || {};

        this.loadDashboard(image._id || null, video._id || null, roomRecord);
    };

    @action
    setStartDate = (date: Date | null): void => {
        this.state.startDate = date;
        this.state.startDatePickerOpen = false;
        // If there is a search term, repeat that search with new date range
        if (this.state.searchTagForm && this.state.searchTagForm.searchTerm) {
            this.searchForTags(this.state.searchTagForm);
        } else {
            this.applyRange();
        }
    };

    @action
    setEndDate = (date: Date | null): void => {
        this.state.endDate = date;
        this.state.endDatePickerOpen = false;
        // If there is a search term, repeat that search with new date range
        if (this.state.searchTagForm && this.state.searchTagForm.searchTerm) {
            this.searchForTags(this.state.searchTagForm);
        } else {
            this.applyRange();
        }
    };

    @action
    setStartDatePickerOpen = (isOpen: boolean): void => {
        this.state.startDatePickerOpen = isOpen;
    };

    @action
    setEndDatePickerOpen = (isOpen: boolean): void => {
        this.state.endDatePickerOpen = isOpen;
    };

    @action
    resetDatePickerOpenStates = (): void => {
        this.state.startDatePickerOpen = false;
        this.state.endDatePickerOpen = false;
    };

    @action
    setSearchTerm = (term: string): void => {
        this.state.searchTagForm.searchTerm = term;
        this.state.searchTagForm.submitted = false;
    };

    private _getChangeSettingsModalController(): ChangeSettingsModalController {
        if (!this.changeSettingsModalController) {
            const changeSettingsState = new ChangeSettingsModalState();
            const modalController = new ChangeSettingsModalController(
                changeSettingsState,
                this.tsAppUserService as any,
                undefined
            );
            runInAction(() => {
                this.changeSettingsModalController = modalController;
            });
            return modalController;
        }
        return this.changeSettingsModalController;
    }

    private _getChangeAccountModalController(): ChangeAccountModalController | undefined {
        if (!this.changeAccountModalController && this.currentUser?.parentSubUserId) {
            const changeAccountState = new ChangeAccountModalState();
            runInAction(() => {
                this.changeAccountModalController = new ChangeAccountModalController(
                    changeAccountState,
                    this.authService,
                    this.appState
                );
            });
        }
        return this.changeAccountModalController;
    }

    @computed
    get isMobileDevice(): boolean {
        return this.env.isMobile(this.displayTabletAsDesktop);
    }

    @computed
    get isIOS(): boolean {
        return this.env.isIOS();
    }

    async handleSettingsClick(): Promise<void> {
        try {
            const modalController = this._getChangeSettingsModalController();
            const updatedUser = await modalController.open({
                currentUser: this.currentUser,
                accountData: this.accountData,
                passwordOnly: false
            });
            if (updatedUser && this.currentUser) {
                Object.assign(this.currentUser, updatedUser);
            }
        } catch (err: any) {
            // User cancelled or error occurred
        }
    }

    handleLogoutClick(): void {
        const accountId = this.accountData?._id;
        const userId = this.currentUser?._id;
        const returnURL = window.location.origin + window.location.pathname;

        this.authService
            .logout(accountId, userId, returnURL)
            .then((res: any) => {
                if (res?.data) {
                    window.location.href = res.data;
                } else {
                    window.location.href = '/';
                }
            })
            .catch(() => {
                window.location.href = '/';
            });
    }

    handleAdminClick(): void {
        this.redirectionService.goToAdmin().catch((err: any) => {
            console.error('Failed to redirect to admin:', err);
        });
    }

    handleConnectivityCheckClick(): void {
        if (this.stateHelper) {
            // The dashboard connectivity check (/connectivityCheck), not the self-service
            // standalone page (/connectivity-self-check). The pre-migration Angular code called
            // safeGo('connectivity'), whose state was registered on the /connectivityCheck url.
            void this.stateHelper.navigateTo(RouteName.CONNECTIVITY_CHECK);
        }
    }

    private _isInConnectivityState(): boolean {
        if (!this.stateHelper || !this.stateHelper.$state) {
            return false;
        }
        return this.stateHelper.$state.current.name === 'connectivity';
    }

    shouldShowConnectivityCheck(): boolean {
        const connectivityCheckEnable = get(this.accountData, 'protectedSettings.connectivityCheck.enable');
        const connectivityCheckPlatform = get(this.accountData, 'protectedSettings.connectivityCheck.platform');
        const platformIncludesDashboard = includes(connectivityCheckPlatform, PlatformType.dashboard);
        const enableLinkToConnectivityCheck = connectivityCheckEnable && platformIncludesDashboard;

        return enableLinkToConnectivityCheck && !this._isInConnectivityState();
    }

    async handleSwitchAccountClick(): Promise<void> {
        const modalController = this._getChangeAccountModalController();
        if (!modalController) {
            return;
        }
        try {
            await modalController.open({
                currentUser: this.currentUser,
                accountData: this.accountData
            });
            window.location.reload();
        } catch (err: any) {
            // User cancelled or error occurred
        }
    }
}
